import type { FunctionComponent } from "react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import style from "./Sidebar.module.css";
import VectorAcceso from "../../../assets/img/Vector_100_Acceso.svg";
import { Routes } from "../../types/enums/Routes.enum";
import type { IdTourValues } from "@/shared/libs/Tour/Tour.config";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { RiAuctionLine, RiCalendar2Line, RiFileList2Line, RiMenuUnfold3Line, RiNewspaperLine, RiScales3Line, RiTeamLine } from "@remixicon/react";

interface SidebarProps {
    MostrarMenuMobile: boolean;
    onToggleMenu: () => void;
    idTour: IdTourValues
}

type IconComponent = React.FC<{ color: string }>;

interface Section {
    id: string;
    label: string;
    icon: IconComponent;
    subitems: { id: string; label: string }[];
}

const Sidebar: FunctionComponent<SidebarProps> = ({ onToggleMenu, MostrarMenuMobile, idTour }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeItem, setActiveItem] = useState("mi-escritorio");
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { canAccessEquipo } = useUserPermissions();
    
    const hoverDelayMs = 500;
    const hoverOpenTimer = useRef<number | null>(null);
    const hoverCloseTimer = useRef<number | null>(null);

    const clearHoverTimers = () => {
        if (hoverOpenTimer.current) {
            window.clearTimeout(hoverOpenTimer.current);
            hoverOpenTimer.current = null;
        }
        if (hoverCloseTimer.current) {
            window.clearTimeout(hoverCloseTimer.current);
            hoverCloseTimer.current = null;
        }
    }

    const handleClick = () => {
        clearHoverTimers();
        setIsOpen(true)
    };

    const handleMouseEnter = () => {
        if (!isMobile) {
            
            if (hoverCloseTimer.current) {
                window.clearTimeout(hoverCloseTimer.current);
                hoverCloseTimer.current = null;
            }
            
            if (!hoverOpenTimer.current) {
                hoverOpenTimer.current = window.setTimeout(() => {
                    setIsOpen(true);
                    hoverOpenTimer.current = null;
                }, hoverDelayMs);
            }
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            
            if (hoverOpenTimer.current) {
                window.clearTimeout(hoverOpenTimer.current);
                hoverOpenTimer.current = null;
            }
            
            if (!hoverCloseTimer.current) {
                hoverCloseTimer.current = window.setTimeout(() => {
                    setIsOpen(false);
                    hoverCloseTimer.current = null;
                }, hoverDelayMs);
            }
        }
    };

    const handleItemClick = (itemId: string) => {
        setActiveItem(itemId);
        handleClick();

        // Navegación basada en el item seleccionado
        switch (itemId) {
            case "mi-escritorio":
                navigate("/");
                break;
            case "casos":
                // No navegar: solo abrir/cerrar el submenu
                break;
            case "ver-casos":
                // Ver listado de casos
                navigate(Routes.CASO_SLASH);
                break;
            case "iniciar-caso":
                navigate(Routes.CASO_CREAR, {
                    state: {
                        esNuevoCaso: true,
                    }
                });
                break;
            case "calendario":
                navigate(`/${Routes.CALENDARIO}`);
                break;
            case "audiencias":
                navigate(`/${Routes.AUDIENCIAS}`);
                break;
            case "archivo":
                navigate(`/${Routes.ARCHIVO}`);
                break;
            case "equipo":
                navigate(`/${Routes.EQUIPO}`);
                break;
            default:
                break;
        }

        if (isMobile) {
            onToggleMenu();
        }
    };  

    // Actualizar el item activo basado en la URL actual
    useEffect(() => {
        const path = location.pathname;
        if (path === "/") {
            setActiveItem("mi-escritorio");
        } else if (path === Routes.CASO_SLASH) {
            // Lista principal de casos -> seleccionamos el subitem 'ver-casos'
            setActiveItem("ver-casos");
        } else if (path.startsWith(Routes.CASO_CREAR)) {
            setActiveItem("iniciar-caso");
        } else if (path.startsWith(Routes.CASO_SLASH)) {
            setActiveItem("casos");
        } else if (path.startsWith(`/${Routes.CALENDARIO}`)) {
            setActiveItem("calendario");
        } else if (path.startsWith(`/${Routes.AUDIENCIAS}`)) {
            setActiveItem("audiencias");
        } else if (path.startsWith(`/${Routes.ARCHIVO}`)) {
            setActiveItem("archivo");
        } else if (path.startsWith(`/${Routes.EQUIPO}`)) {
            setActiveItem("equipo");
        }
    }, [location.pathname]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 600);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
            clearHoverTimers();
        };
    }, []);

    const sections: Section[] = [
        {
            id: "mi-escritorio",
            label: t('sidebar.myDesk'),
            icon: ({ color }) => <RiNewspaperLine size={24} color={color}/>,
            subitems: [],
        },
        {
            id: "casos",
            label: t('sidebar.cases'),
            icon: ({ color }) => <RiScales3Line size={24} color={color}/>,
            subitems: [
                { id: "ver-casos", label: t('sidebar.viewCases') },
                { id: "iniciar-caso", label: t('sidebar.startCase') },
                /* { id: "Certificacion", label: "Certificación" },
                { id: "Amparo", label: "Amparo" }, */
            ],
        },
        {
            id: "audiencias",
            label: t('sidebar.hearings'),
            icon: ({ color }) => <RiAuctionLine size={24} color={color}/>,
            subitems: [
                /* { id: "Fijacion", label: "Fijación" },
                { id: "Certificacion-titulo", label: "Certificación título" }, */
            ],
        },
        {
            id: "calendario",
            label: t('sidebar.calendar'),
            icon: ({ color }) => <RiCalendar2Line size={24} color={color}/>,
            subitems: [],
        },
        {
            id: "archivo",
            label: t('sidebar.files'),
            icon: ({ color }) => <RiFileList2Line size={24} color={color}/>,
            subitems: [
                /* { id: "deposito-archivo", label: "Depósito de archivo" }, */
            ],
        },
        ...(canAccessEquipo ? [{
            id: "equipo",
            label: t('sidebar.team'),
            icon: ({ color }: any) => <RiTeamLine size={24} color={color}/>,
            subitems: [],
        }] : [])

    ];

    return (
        <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`
                bg-[#F1F1F1] shadow-lg flex flex-col transition-all duration-900
                ${isMobile
                    ? `${style.ContentMenuMobile} max-[600px]:h-full w-full ${MostrarMenuMobile ? "block" : "hidden"}`
                    : isOpen
                        ? "w-[15%] max-[1150px]:w-[25%] max-[800px]:w-[40%]"
                        : "w-[4%] max-[1150px]:w-[8%] max-[900px]:w-[10%]"
                }
            `}
            id={idTour}
        >
            {/* Encabezado */}
            {isMobile ? (
                <div className="flex items-center gap-3 p-3 hover:bg-gray-200 cursor-pointer rounded-lg">
                    <button
                        onClick={onToggleMenu}
                        className="flex items-center text-[#5B5B5B] text-[24px] hover:bg-gray-200"
                    >
                        <span className="flex justify-center items-center w-8 h-8 ">
                            <RiMenuUnfold3Line size={24} color={"#5B5B5B"} />
                        </span>
                        <h1 className="ml-1">{t('sidebar.menu')}</h1>
                    </button>
                </div>
            ) : (
                <>
                    {!isOpen ? (
                        <div
                            onClick={handleClick}
                            className="flex justify-center items-center gap-3 py-3 hover:bg-gray-200 cursor-pointer rounded-lg"
                        >
                            <span className="flex justify-center items-center w-8 h-8 ">
                                <RiMenuUnfold3Line size={24} color={"#5B5B5B"} />
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 p-3 hover:bg-gray-200 rounded-lg">
                            <div
                                className="flex items-center text-[#5B5B5B] text-[24px] rounded-full"
                            >
                                <h1 className="ml-1">{t('sidebar.menu')}</h1>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Navegación */}
            <nav
                className={` max-[600px]:h-[40rem] h-[34rem] ${isOpen ? "py-4" : "py-4 max-[1790px]:px-1 max-[2000px]:px-1 max-[900px]:px-2"
                    } `}
            >
                <div className={`${style.Scrollbar} h-full overflow-y-auto`}>
                    {sections.map((section) => {
                        const iconColor = activeItem === section.id ? "#EC3340" : "#5B5B5B";
                        return section.subitems.length > 0 ? (
                            <details className={`group ${activeItem === section.id ? 'bg-[#E5E5E5]' : ''}`} key={section.id} >
                                <summary
                                    className={`flex items-center ${isOpen ? `justify-between` : `${isMobile ? '':'justify-center'}`
                                        } gap-3 p-2 rounded-lg hover:bg-gray-200 cursor-pointer list-none relative ${activeItem === section.id
                                            ? "text-[#EC3340] font-bold bg-[#E5E5E5]"
                                            : "text-[#5B5B5B]"
                                        }`}
                                    onClick={() => handleItemClick(section.id)}
                                >
                                    {activeItem === section.id && (
                                        <span className="absolute left-0 top-0 w-1 h-full bg-[#EC3340] rounded-r-md"></span>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`flex justify-center items-center`}
                                        >
                                            <section.icon color={iconColor} />
                                        </span>
                                        {isMobile ?
                                            <>
                                                <span className="text-sm">{section.label}</span>
                                            </> :
                                            <>

                                                {isOpen && <span className="text-sm">{section.label}</span>}
                                            </>
                                        }
                                    </div>
                                    {isMobile ?
                                        <>
                                            <span className="flex group-open:rotate-180 transition-transform d-icon d-arrowhead-down"></span>
                                        </>
                                        :
                                        <>
                                            {isOpen && (
                                                <span className="flex group-open:rotate-180 transition-transform d-icon d-arrowhead-down"></span>
                                            )}
                                        </>
                                    }
                                </summary>
                                {isOpen && (
                                    <div className={`text-[#5B5B5B] ml-8 mt-1 flex flex-col gap-1 text-sm`}>
                                        {section.subitems.map((sub) => (
                                            <span
                                                key={sub.id}
                                                className={`p-1 rounded hover:bg-gray-200 hover:font-bold cursor-pointer relative ${activeItem === sub.id ? "font-medium" : ""
                                                    }`}
                                                onClick={() => handleItemClick(sub.id)}
                                            >
                                                {activeItem === sub.id && (
                                                    <span className="absolute left-0 top-0 w-1 h-full bg-[#EC3340] rounded-r-md"></span>
                                                )}
                                                <span className="ml-2 flex">{sub.label}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </details>
                        ) : (
                            <div
                                key={section.id}
                                className={` ${isOpen ? '' : `${isMobile ? '':'justify-center'}`} flex items-center gap-3 p-2 rounded-lg hover:bg-gray-200 cursor-pointer relative ${activeItem === section.id
                                    ? "text-[#EC3340] font-bold bg-[#E5E5E5]"
                                    : "text-[#5B5B5B]"
                                    }`}
                                onClick={() => handleItemClick(section.id)}
                            >
                                {activeItem === section.id && (
                                    <span className="absolute left-0 top-0 w-1 h-full bg-[#EC3340] rounded-r-md"></span>
                                )}
                                <span
                                    className={`flex justify-center items-center`}
                                >
                                    <section.icon color={iconColor} />
                                </span>
                                {isMobile ?
                                    <>
                                        <span className="text-sm">{section.label}</span>
                                    </> :
                                    <>

                                        {isOpen && <span className="text-sm">{section.label}</span>}
                                    </>
                                }

                            </div>
                        )
                    }
                    )}
                </div>
            </nav>

            {/* Imagen final */}
            {(isOpen || isMobile) && (
                <div className="mt-auto mb-[2rem] flex justify-center">
                    <img src={VectorAcceso} alt="Acceso" />
                </div>
            )}
        </div>
    );
};

export default Sidebar;
